import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { Reveal, SectionLabel } from "./Reveal";
import { OrnamentLine } from "./Ornament";
import { Emblem } from "./Logo";

export const Region = () => {
  const { t } = useLang();
  const r = t.region;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} id="region" className="relative isolate overflow-hidden py-28 md:py-40" data-testid="region-section">
      <motion.div style={{ y }} className="absolute inset-[-14%_0] -z-10">
        <img loading="lazy" decoding="async" src="/assets/region-bozzhyra.webp" alt="Бозжыра, Маңғыстау" className="w-full h-full object-cover kenburns kb-c" />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,#0A0D14_0%,rgba(10,13,20,0.55)_35%,rgba(10,13,20,0.7)_70%,#0A0D14_100%)]" />

      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
        <SectionLabel n="✦">{r.label}</SectionLabel>
        <Reveal>
          <h2 className="font-display text-4xl md:text-7xl mt-8 text-[var(--text)] max-w-4xl leading-[1.02]" data-testid="region-title">{r.title}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 text-base md:text-xl text-[var(--text)]/90 max-w-2xl leading-relaxed">{r.text}</p>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-10 font-serif-accent text-3xl md:text-5xl text-[var(--gold)]">«{r.quote}»</p>
        </Reveal>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.7, rotate: -40 }}
          whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative mx-auto w-[min(78vw,440px)] aspect-square"
          data-testid="region-emblem"
        >
          <div className="absolute inset-[10%] rounded-full bg-[var(--gold)]/25 blur-[80px] badge-glow" aria-hidden="true" />
          <div className="absolute inset-0 rounded-full border border-[var(--gold)]/25" aria-hidden="true" />
          <div className="absolute inset-[-5%] rounded-full border border-dashed border-[var(--gold)]/20 spin-rev" aria-hidden="true" />
          <Emblem className="relative w-full h-full drop-shadow-[0_20px_60px_rgba(0,0,0,0.6)]" spin glow />
        </motion.div>
        </div>
        <OrnamentLine className="mt-16 max-w-xl" />
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {r.facts.map((f, i) => (
            <Reveal key={i} delay={0.1 * i} y={24}>
              <div className="rounded-3xl border border-white/15 bg-[#0A0D14]/55 backdrop-blur-xl p-6 h-full transition-colors duration-300 hover:border-[var(--gold)]/60" data-testid={`region-fact-${i}`}>
                <div className="font-display text-3xl md:text-4xl text-[var(--gold)]">{f.v}</div>
                <div className="mt-3 text-sm text-[var(--text)]/90">{f.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
